import { memo, useCallback, useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { cn } from '@divlab/divanui';
import { IconArrowUp } from '@divlab/divanui/icons';

import useDeviceInfo from '@Hooks/useDeviceInfo';
import useMedias from '@Hooks/useMedias';
import useScrollPosition from '@Hooks/useScrollPosition';
import styles from './PageUp.module.css';

import type { FC, HTMLAttributes } from 'react';

export interface PageUpProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const PageUp: FC<PageUpProps> = (props) => {
  const { className, ...restProps } = props;
  const [isShowButton, setIsShowButton] = useState(true);
  const [visible, setVisible] = useState(false);
  const { os, browser } = useDeviceInfo();
  const { isMobile } = useMedias();

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  useScrollPosition(({ current, previous }) => {
    const isUp = previous.y > current.y;

    if (previous.y === current.y) return;

    if (isUp && current.y !== 0) {
      setVisible(true);
    } else {
      setVisible(false);
    }

    setTimeout(() => {
      if (current.y === window.scrollY) {
        setVisible(false);
      }
    }, 3000);
  });

  useEffect(() => {
    setIsShowButton(
      !(
        os === 'Android' &&
        (browser === 'YaBrowser' || browser === 'Samsungbrowser' || browser === 'Opera') &&
        isMobile
      ),
    );
  }, [browser, isMobile, os]);

  return (
    isShowButton && (
      <CSSTransition
        unmountOnExit
        classNames={{
          enter: styles.enter,
          enterActive: styles.enterActive,
          exitActive: styles.exitActive,
          exit: styles.exit,
        }}
        timeout={400}
        in={visible}
      >
        <div className={cn(styles.up, className)} {...restProps}>
          <div className={styles.circle} onClick={scrollToTop}>
            <IconArrowUp className={styles.arrow} />
          </div>
        </div>
      </CSSTransition>
    )
  );
};

export default memo(PageUp);
